import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, Stack, useTheme } from "@mui/material";
import { useEffect } from "react";
import { ScrollRestoration, matchPath, matchRoutes, useLoaderData, useLocation, useNavigate, } from "react-router-dom-v5-compat";
import { setupInterceptors } from "../api/axios";
import { useAuth } from "../AuthContext";
import { NavPortalRoots } from "../OrderFlow/utils/orderFlowUtils";
import { RedirectionOutlet } from "../RedirectionOutlet";
import { notFoundRoutes, routesThatHideHeader, routesThatUseBgColor, sffMobileRoutes } from "./RootLayout-route-utils";
import { useBranding } from "../Shared/components/BrandingProvider/BrandingProvider";
import { Footer } from "../Shared/components/Footer/Footer";
import { NavBar } from "../Shared/components/Navigation/NavBar";
import { Routes, isValidUrl } from "../Shared/components/Navigation/navigationUtils";
import { useTopBar } from "../Shared/components/TopBar/useTopBar";
import { useContainerSettings } from "../Shared/hooks/UseContainerSettings/useContainerSettings";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { useRouteMatch } from "../Shared/hooks/useRouteMatch";
import { default_background_color } from "../Shared/utils/constants";
export const RootLayout = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const theme = useTheme();
    const { containerPadding, containerWidth, isMobile, openedFromMobileApp } = useContainerSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const { isGiftCardFlow, isAccountSection, isThemePage } = useRouteMatch();
    const authContext = useAuth();
    const { isAuthenticated } = authContext;
    const { datadogLog } = useLogging();
    useEffect(() => {
        setupInterceptors(navigate, location, authContext);
    }, []);
    const bgRouteMatch = matchRoutes(routesThatUseBgColor, location);
    const headerRouteMatch = matchRoutes(routesThatHideHeader, location);
    const sffRouteMatch = matchRoutes(sffMobileRoutes, location);
    const notFoundRouteMatch = matchRoutes(notFoundRoutes, location);
    const { generalSettings } = useLoaderData();
    const { componentToRender, usesTopBar } = useTopBar(generalSettings);
    const { branding } = useBranding();
    const bgColorPath = (_b = (_a = bgRouteMatch === null || bgRouteMatch === void 0 ? void 0 : bgRouteMatch[0]) === null || _a === void 0 ? void 0 : _a.route) === null || _b === void 0 ? void 0 : _b.path;
    const headerPath = (_d = (_c = headerRouteMatch === null || headerRouteMatch === void 0 ? void 0 : headerRouteMatch[0]) === null || _c === void 0 ? void 0 : _c.route) === null || _d === void 0 ? void 0 : _d.path;
    const sffPath = (_f = (_e = sffRouteMatch === null || sffRouteMatch === void 0 ? void 0 : sffRouteMatch[0]) === null || _e === void 0 ? void 0 : _e.route) === null || _f === void 0 ? void 0 : _f.path;
    const notFoundPath = (_h = (_g = notFoundRouteMatch === null || notFoundRouteMatch === void 0 ? void 0 : notFoundRouteMatch[0]) === null || _g === void 0 ? void 0 : _g.route) === null || _h === void 0 ? void 0 : _h.path;
    const defaultRedirectUrl = generalSettings.DefaultRedirectUrl;
    let backgroundColor = bgColorPath === Routes.INDEX || bgColorPath === Routes.NOT_FOUND
        ? default_background_color
        : bgColorPath === Routes.LOGIN ||
            bgColorPath === Routes.REGISTER ||
            bgColorPath === Routes.EDIT_FLOW ||
            bgColorPath === Routes.SKU ||
            bgColorPath === Routes.CHECKOUT ||
            bgColorPath === Routes.THANK_YOU ||
            bgColorPath === Routes.ORDER_FLOW
            ? branding.SiteBodyBg.Color
            : theme.palette.common.white;
    const isPaymentConfirmation = matchPath({ path: "/PaymentStatus" }, location.pathname);
    if (isPaymentConfirmation) {
        backgroundColor = "white";
    }
    if (isAccountSection) {
        backgroundColor = branding.SiteBodyBg.Color;
    }
    const isLoginOrRegister = headerPath === Routes.INDEX || headerPath === Routes.LOGIN || headerPath === Routes.REGISTER;
    const isSffChromelessView = (sffPath === Routes.SKU ||
        sffPath === Routes.ORDER_FLOW ||
        sffPath === Routes.EDIT_FLOW ||
        sffPath === Routes.CHECKOUT ||
        sffPath === Routes.THANK_YOU) &&
        openedFromMobileApp;
    const isPageNotFound = notFoundPath === Routes.NOT_FOUND;
    // If a user tries to access a protected route without authentication they are redirected to login.
    // We want to hide the header in this case.
    const hideIfNotAuthenticated = isAccountSection && !isAuthenticated;
    const shouldHideHeader = isLoginOrRegister || isSffChromelessView || isPageNotFound || isPaymentConfirmation || hideIfNotAuthenticated;
    const shouldHideFooter = isSffChromelessView || isPaymentConfirmation;
    const isIndexRoute = matchPath({ path: Routes.INDEX }, location.pathname);
    const topBarContent = isGiftCardFlow ? generalSettings.TopBarContentGiftCard : generalSettings.TopBarContentOrderFlow;
    const showTopBar = usesTopBar && !!topBarContent && !shouldHideHeader && !isAccountSection && !isThemePage;
    //If the defaultRedirectUrl is configured and valid and the index route is hit send the customer to the defaultRedirectUrl.
    if (!!(isIndexRoute === null || isIndexRoute === void 0 ? void 0 : isIndexRoute.pathname) && defaultRedirectUrl) {
        if (isValidUrl(defaultRedirectUrl)) {
            window.location.replace(defaultRedirectUrl);
            return _jsx(_Fragment, {});
        }
        datadogLog(`DefaultRedirectUrl is not a valid URL. Value: ${defaultRedirectUrl} `, LogLevel.WARN);
    }
    // ===== CONTAINER SETTINGS =====
    const containerMargins = isMobile ? {} : { mt: 1.5, mb: 1.5 };
    // This creates the "paper" effect for the account pages.
    const swankyStyles = isAccountSection
        ? Object.assign({ boxShadow: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }, containerMargins) : {};
    return (_jsxs(Stack, Object.assign({ id: "layout-wrapper", minHeight: "100vh", bgcolor: backgroundColor }, { children: [!shouldHideHeader && (_jsx(NavBar, { linksContent: componentToRender, showTopBar: showTopBar, content: topBarContent, 
                // TODO! @MUI -- not yet integrated into theme
                topBarBackgroundColor: generalSettings.TopBarBackgroundColor })), _jsx(Container, Object.assign({ id: "content-container", maxWidth: containerWidth, disableGutters: isMobile || openedFromMobileApp ? true : false, sx: Object.assign(Object.assign({ flexGrow: 1 }, containerPadding), swankyStyles) }, { children: _jsxs(Stack, Object.assign({ id: "main-content-wrapper", minHeight: "100%", justifyContent: "center", flexGrow: 1 }, { children: [_jsx(RedirectionOutlet, {}), _jsx(ScrollRestoration, {})] })) })), _jsx("div", { id: NavPortalRoots.Pinned, style: { position: "sticky", bottom: 0, zIndex: 1100 } }), _jsx("div", { id: NavPortalRoots.Unpinned, style: { width: "100%" } }), !shouldHideFooter && (_jsx(Box, Object.assign({ id: "footer-wrapper" }, { children: _jsx(Footer, {}) })))] })));
};
